import { useEffect } from 'react';
import { shouldAllowTouchMoveEvent, shouldAllowWheelEvent } from './background-scroll-lock.ts';

/**
 * Blocks wheel/touch scrolling of nested page scrollers while a modal is open.
 * Headless UI only locks documentElement overflow; browse (and similar) use
 * inner `overflow-y-auto` regions that still scroll without this.
 *
 * Mark modal scroll surfaces with `data-scroll-lock-allow` so they remain usable.
 * Portaled Headless UI list/popover surfaces (`data-headlessui-portal`) may scroll
 * when they contain overflow content without unlocking the page behind the modal.
 */
export const useLockBackgroundScroll = (active: boolean) => {
  useEffect(() => {
    if (!active) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      let deltaX = event.deltaX;
      let deltaY = event.deltaY;

      if (deltaX === 0 && event.shiftKey && deltaY !== 0) {
        deltaX = deltaY;
        deltaY = 0;
      }

      if (shouldAllowWheelEvent(event.target, deltaY, deltaX)) {
        return;
      }
      event.preventDefault();
    };

    const onTouchMove = (event: TouchEvent) => {
      if (shouldAllowTouchMoveEvent(event.target)) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [active]);
};
